import { Box, Stack, Text, Button, ExpandMoreIcon, ExpandLessIcon } from '@citibox-panel/ui';
import type { DeliveryDetail } from '@citibox-panel/api-client';

export interface DeliveryLocationProps {
  detail: DeliveryDetail;
  // The «buzón» activity log lives under CERRADURA: `canViewLog` gates the toggle (only when a
  // box is/was associated), `logOpen` drives its label/chevron/aria state, `onToggleLog` flips it.
  canViewLog?: boolean;
  logOpen?: boolean;
  onToggleLog?: () => void;
}

// Where the parcel is: address (one formatted line) and lock (qr · model), each under an
// overline section label. Both live one level down since CBX-10954 — the dirección inside
// `location`, the cerradura inside the «buzón» that carries it. Rendered as React text
// (default-escaped); address and lock are «Usuario»/locker PII, so they are shown but never logged
// (lens 5). Absent values → em dash. When a box is associated, a disclosure toggle unfolds the box
// activity log below.
export function DeliveryLocation({
  detail,
  canViewLog = false,
  logOpen = false,
  onToggleLog,
}: DeliveryLocationProps) {
  // A lock with no model registered arrives as `model: null` — render the qr alone rather than a
  // dangling separator. The devices id (`lock.id`) is a read key, never shown. No «buzón» means no
  // cerradura either: it hangs off `box`, so the two absences collapse into the same em dash.
  const detailLock = detail.box?.lock ?? null;
  const lock =
    detailLock === null
      ? '—'
      : [detailLock.qr, detailLock.model].filter((part) => part !== null).join(' · ');
  return (
    <Stack spacing={1.5}>
      {/* TODO: i18n — copy en ES hasta Fase 1. */}
      <Box>
        <Text variant="overline" color="secondary" bold dense>
          DIRECCIÓN
        </Text>
        <Text variant="body2">{detail.location.address ?? '—'}</Text>
      </Box>
      <Box>
        <Text variant="overline" color="secondary" bold dense>
          CERRADURA
        </Text>
        <Text variant="body2">{lock}</Text>
      </Box>
      {canViewLog && (
        // A quiet outlined pill that hugs its label (the Stack would stretch it to the
        // column width), per the design: a secondary control, not a purple action.
        <Box sx={{ alignSelf: 'flex-start' }}>
          <Button
            variant="outlined"
            color="neutral"
            aria-expanded={logOpen}
            aria-controls="locker-log-region"
            endIcon={logOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            {...(onToggleLog ? { onClick: onToggleLog } : {})}
          >
            {/* TODO: i18n */}
            {logOpen ? 'Ocultar log buzón' : 'Ver log buzón'}
          </Button>
        </Box>
      )}
    </Stack>
  );
}
